import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  linkedWorkItemToPresentableTask,
  resolveNativeLinkedWorkQueryScope,
  TIMELY_TOOL_ID,
} from '~/api/linkedWorkItems/linkedWorkItems.helpers';
import { useNativeLinkedWorkBrowser } from '~/api/linkedWorkItems/useNativeLinkedWorkBrowser';
import { useGetTasksQuery } from '~/redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import {
  TIC_LINKED_WORK_SCOPE,
  TIC_SHOW_ACTIVE_TASKS,
  TIC_SHOW_COMPLETED_TASKS,
} from '~constants/user';
import { useDebouncedValue } from '~hooks/useDebouncedValue';
import TimelyLogo from '~images/apps_logo/timely.svg';
import {
  resolveLinkedWorkScope,
  type TicLinkedWorkMode,
} from '~redux/modules/tic/tasks/tic.tasks.scope';
import {
  getAssignedToMe,
  getLinkedWorkMode,
  getShowActiveTasks,
  getShowCompletedTasks,
} from '~redux/modules/tic/tasks/tic.tasks.selectors';
import { getTicTasks } from '~redux/modules/tic/tasks/tic.tasks.slice';
import { uniqueTaskResourcesByUri } from '~redux/modules/tic/tasks/tic.tasks.utils';
import { useScopedTaskBrowser } from '~redux/modules/tic/tasks/useScopedTaskBrowser';
import {
  getContextualTicAccounts,
  getHasSettledWithoutTicWorkspace,
} from '~redux/modules/tic/tic.auth.selectors';
import { saveUserProperty } from '~redux/modules/user.actions';
import { getHasLinkedWorkItemsSupport } from '~redux/selectors/accounts';
import { getEnrichedProjectsForCurrentUserById } from '~redux/selectors/projects';
import type { ThunkDispatch } from '~types/redux';

// One keyset page; infinite scroll reaches the remaining rows.
const INITIAL_FETCH_TASKS_LIMIT = 50;
const SEARCH_TASKS_LIMIT = 30;
// Text search is TIC's most expensive query: one scan per typing pause, not per keystroke. 300ms
// is what every other search input in the app waits (Search, ProjectSelect, the filter dropdowns).
const SEARCH_DEBOUNCE_MS = 300;
// Below this the query matches too much to be worth that scan, and the dropdown narrows the loaded
// page locally anyway. The hour editor's picker (TicTasksInHourEditor) has always gated here.
const SEARCH_MIN_CHARS = 3;

export interface LinkedWorkOption {
  id: string;
  label: string;
  value: string;
  key: string;
  toolId: string;
  toolName: string;
  toolIconUrl?: string;
  isCompleted: boolean;
  description?: string;
  url?: string;
}

export interface GroupedLinkedWorkOptions {
  /**
   * Identity of the group, unique within one list. Not `toolId`: every native linked work item
   * carries the same `tool_id` ('timely') but names its own source, so one tool id can head several
   * groups ("Jira", "GitHub", "Timely"). Use this — never `toolId` — wherever a group needs a key.
   */
  groupKey: string;
  toolId: string;
  toolName: string;
  toolIconUrl?: string;
  options: LinkedWorkOption[];
}

export interface UseLinkedWorkOptionsParams {
  /**
   * The entry/row's Timely project id. When provided (and the project is resolvable), the dropdown
   * options are narrowed to that project per the persisted linked-work scope. Pass `null`/omit for
   * project-less contexts (pending entries, batch reassign) — the list then spans all integrations.
   */
  projectId?: number | null;
}

// Group identity is the tool id AND the displayed tool name. For TIC tools the name is a property
// of the tool, so this is the tool id by another route. Native linked work items all share the
// 'timely' tool id and carry a per-item source name, so keying on the id alone would collapse every
// source into one group labelled after whichever item happened to come first. JSON-encoded rather
// than concatenated so no separator character can appear inside a title and flatten two different
// (id, name) pairs onto one key.
const groupKeyFor = (task: PresentableTaskResource): string =>
  JSON.stringify([task.tool_id, task.tool.title]);

const toGroupedOptions = (taskResources: PresentableTaskResource[]): GroupedLinkedWorkOptions[] => {
  const tasksByTool = taskResources.reduce(
    (acc, task) => {
      const groupKey = groupKeyFor(task);
      if (!acc[groupKey]) {
        acc[groupKey] = { toolId: task.tool_id, tool: task.tool, tasks: [] };
      }
      acc[groupKey].tasks.push(task);
      return acc;
    },
    {} as Record<
      string,
      { toolId: string; tool: PresentableTaskResource['tool']; tasks: PresentableTaskResource[] }
    >
  );

  return Object.entries(tasksByTool).map(([groupKey, { toolId, tool, tasks }]) => ({
    groupKey,
    toolId,
    toolName: tool.title,
    // A native group is a source name, which has no icon of its own; `tool` is just the first
    // item's, and its icon_url is that item's own icon.
    toolIconUrl: toolId === TIMELY_TOOL_ID ? TimelyLogo : (tool.icon_url ?? undefined),
    options: tasks.map(task => ({
      id: task.id,
      label: task.title,
      value: task.id,
      key: task.metadata?.external_code ?? '',
      toolId: task.tool_id,
      toolName: tool.title,
      toolIconUrl: task.tool.icon_url ?? undefined,
      isCompleted: task.completed_at != null,
      description: task.metadata?.description ?? undefined,
      url: task.url_html ?? undefined,
    })),
  }));
};

/**
 * Hook to fetch and transform linked work into dropdown options, from both sources: TIC task
 * resources and native (Rails-backed) linked work items. Either source can be the only one an
 * account has, so each is fetched, errored and awaited independently.
 *
 * Mirrors the manual-logging linked-work picker: the dropdown `groupedOptions` are narrowed by the
 * persisted scope (all / same-integration / same-project, resolved against `params.projectId`) and
 * status (Open/Done) preferences, while `flatOptions` stays UNFILTERED so the already-selected chip
 * and global label lookups can always resolve — an active scope can never hide a linked task.
 *
 * Does NOT answer "should the linked-work surface exist" — that is `getHasLinkedWorkPicker`,
 * an account fact. Everything returned here is downstream of a fetch the user's own filters can
 * legitimately reduce to nothing, so a visibility gate derived from it would hide its own escape.
 *
 * @param searchQuery - Optional raw search input; the API search fires once typing settles, and
 *   only from the third character on. Shorter queries browse and narrow locally.
 * @param params - Optional per-row context (the project to scope by).
 */
export const useLinkedWorkOptions = (searchQuery?: string, params?: UseLinkedWorkOptionsParams) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { workspace } = useSelector(getContextualTicAccounts);
  // Durably no workspace — the browse query is skipped and will never run. Kept distinct from a
  // session that is merely still resolving; see the selector's own note.
  const hasSettledWithoutWorkspace = useSelector(getHasSettledWithoutTicWorkspace);
  // Native (Rails-backed) linked work items are a second, independent source for this same list.
  // They need no TIC workspace, session or connection, so an account can have them and nothing else.
  const hasLinkedWorkItemsSupport = useSelector(getHasLinkedWorkItemsSupport);

  // Use accumulated tasks from Redux for lookups (includes all previously fetched tasks)
  const allTasks = useSelector(getTicTasks);

  // Persisted filter preferences — shared with the manual-logging picker (single source of truth).
  const linkedWorkMode = useSelector(getLinkedWorkMode);
  const assignedToMe = useSelector(getAssignedToMe);
  const showActive = useSelector(getShowActiveTasks);
  const showCompleted = useSelector(getShowCompletedTasks);

  // Resolve the row's project from the same loggable-projects list the picker offers. We read
  // `.base` (which always carries `tic` + `id` for synced projects, even when the full project
  // hasn't been fetched), mirroring the manual-logging selector. `useFullProject` alone would miss
  // not-yet-fetched projects and silently disable scope filtering. The sentinel `0` (pending entry)
  // resolves to no project.
  const projectId = params?.projectId ?? null;
  const projectsById = useSelector(getEnrichedProjectsForCurrentUserById);
  const project = projectId && projectId !== 0 ? projectsById[projectId] : undefined;

  const scope = useMemo(
    () =>
      resolveLinkedWorkScope({
        showActive,
        showCompleted,
        linkedWorkMode,
        assignedToMe,
        isProjectSelected: project != null,
        projectTic: project?.base.tic,
        selectedProjectId: project?.base.id ?? null,
      }),
    // The status toggles are inputs to the scope now, so they belong here — without them a toggle
    // flip would leave the memoized scope (and therefore the query) unchanged.
    [linkedWorkMode, assignedToMe, project, showActive, showCompleted]
  );

  // Only the server lookup waits for the typing pause; the dropdown keeps narrowing the
  // already-loaded options, so the list still responds to every keystroke. The `?? ''` is the one
  // place `undefined` is normalized: an `undefined`→`''` transition is not a change the user made,
  // and must not open a debounce window.
  // Until the newest keystrokes reach the server an empty list is the dropdown's LOCAL filter over
  // the loaded page, not the scope's answer — so the window is unresolved, not empty.
  const { value: debouncedSearchQuery, isPending: isDebouncePending } = useDebouncedValue(
    searchQuery ?? '',
    SEARCH_DEBOUNCE_MS
  );
  const trimmedSearch = debouncedSearchQuery.trim();
  const hasSearchQuery = trimmedSearch.length >= SEARCH_MIN_CHARS;

  // Browse list: server-authoritative and paginated (see useScopedTaskBrowser). The server applies
  // the active scope (none for `all`, `toolId` for integration, `projectUri` for project), so the
  // picker pages through the scope's FULL task set on scroll instead of client-filtering only the
  // first page of recent rows (the TT-5457 bug). Paused while text-searching.
  const browser = useScopedTaskBrowser({
    accountId: workspace?.id,
    taskQueryScope: scope.taskQueryScope,
    pageSize: INITIAL_FETCH_TASKS_LIMIT,
    skip: !workspace?.id || hasSearchQuery,
  });

  // Search is a separate server lookup, also scope-narrowed, that drives the list while active.
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    error: searchError,
  } = useGetTasksQuery(
    {
      accountId: workspace?.id ?? '',
      limit: SEARCH_TASKS_LIMIT,
      search: trimmedSearch,
      ...scope.taskQueryScope,
    },
    {
      // `status: 'none'` cannot match — the server answers it with `AND FALSE` — so skip rather
      // than spend a round trip per keystroke on a known-empty result. Same reasoning as the
      // browse path in `useScopedTaskBrowser`; the search path has to repeat it because it builds
      // its own query.
      skip: !workspace?.id || !hasSearchQuery || scope.taskQueryScope.status === 'none',
    }
  );

  // The native source, fetched exactly the way the manual-logging picker fetches it
  // (TicTasksInHourEditor): narrowed SERVER-side rather than re-filtered on the client, with
  // `project_id` whenever the active MODE ties the list to the row's project and the Open/Done
  // toggles mapped onto the endpoint's `status`. `status: 'none'` cannot match, so it is skipped
  // rather than round-tripped. Browse and search each page by offset, so infinite scroll reaches
  // every native row, not only the first page.
  const nativeScopeArgs = useMemo(() => resolveNativeLinkedWorkQueryScope(scope), [scope]);
  const skipNative = !hasLinkedWorkItemsSupport || scope.taskQueryScope.status === 'none';
  const nativeBrowse = useNativeLinkedWorkBrowser({
    scopeArgs: nativeScopeArgs,
    pageSize: INITIAL_FETCH_TASKS_LIMIT,
    skip: skipNative || hasSearchQuery,
  });
  const nativeSearch = useNativeLinkedWorkBrowser({
    scopeArgs: { ...nativeScopeArgs, search: trimmedSearch },
    pageSize: SEARCH_TASKS_LIMIT,
    skip: skipNative || !hasSearchQuery,
  });
  const nativeDriving = hasSearchQuery ? nativeSearch : nativeBrowse;

  const isLoading =
    (hasSearchQuery ? isSearchLoading : browser.isInitialLoading) ||
    (!skipNative && nativeDriving.isLoading);
  // `isResolving` must follow isFetching, not isLoading. RTK Query sets `isLoading` only for a
  // hook's FIRST ever load and retains `data` across arg changes, so a SECOND search keeps the
  // previous (possibly empty) payload with isLoading=false — and the list would render the
  // authoritative "nothing matches" while a request is still in flight (TT-5457).
  const isFetchingCurrent =
    isDebouncePending || (hasSearchQuery ? isSearchFetching : browser.isInitialLoading);
  // Whichever request is DRIVING the list owns the error. TIC is the primary source: while a TIC
  // workspace exists (or may still arrive), a failed native fetch only means native contributes no
  // rows, and the TIC rows alongside it are real — so the failure copy follows TIC alone. Native
  // owns the error only once TIC is durably absent: on a native-only account the TIC queries never
  // run, and reading only TIC there would leave a failed native fetch rendering the authoritative
  // "no linked work found". Mirrors the manual-logging picker (TicTasksInHourEditor).
  const ticError = hasSearchQuery ? searchError : browser.error;
  const nativeError = skipNative || !hasSettledWithoutWorkspace ? undefined : nativeDriving.error;
  const error = ticError ?? nativeError;
  // "An empty list is not authoritative yet", read PER SOURCE. Each source can be durably absent on
  // its own — a tenant with no TIC workspace never dispatches a browse, an account without the
  // native feature never dispatches a linked-work-items query — and a skipped query never settles.
  // Reading the two together is what left the no-workspace tenant resolving forever (TT-5457);
  // reading only TIC is what would make a native-only account claim "nothing found" mid-fetch.
  const isTicResolving =
    !hasSettledWithoutWorkspace && (isFetchingCurrent || (!hasSearchQuery && !browser.hasFetched));
  const isNativeResolving =
    !skipNative &&
    (isDebouncePending ||
      nativeDriving.isFetching ||
      (!hasSearchQuery && !nativeBrowse.hasFetched));
  const scrollResetKey = hasSearchQuery
    ? `${browser.browseKey}|search:${trimmedSearch}`
    : `${browser.browseKey}|browse`;

  // Pool (all fetched tasks) backs the UNFILTERED flatOptions — the selected chip + label lookups
  // must always resolve regardless of the active scope, so this stays unscoped.
  const taskResources = useMemo<PresentableTaskResource[]>(() => {
    if (!allTasks) return [];
    return allTasks.filter(
      (resource): resource is PresentableTaskResource =>
        'type' in resource && resource.type === 'task'
    );
  }, [allTasks]);

  // UNFILTERED groups → flatOptions (lookups). Never scope/status narrowed.
  const allGroupedOptions = useMemo(() => toGroupedOptions(taskResources), [taskResources]);

  const flatOptions = useMemo<LinkedWorkOption[]>(
    () => allGroupedOptions.flatMap(group => group.options),
    [allGroupedOptions]
  );

  // The dropdown source: scope-narrowed server browse pages, or the (also scope-narrowed) search
  // results while searching. Server-authoritative — no client scope re-filter here.
  // Native items are their own already-scoped result set (see `nativeScopeArgs`), paged by their
  // own browser and merged after the TIC rows — the TIC browser pages a TIC cursor and has no second
  // source to interleave. They are mapped from the native browser's pages, not read back out of the
  // shared pool, so a stale pool entry from an earlier, wider scope can never reappear in a narrowed
  // list; the unfiltered `flatOptions` above still label existing links from the pool.
  const nativeTasks = useMemo<PresentableTaskResource[]>(
    () =>
      skipNative
        ? []
        : nativeDriving.items.map(item => linkedWorkItemToPresentableTask(item, TimelyLogo)),
    [skipNative, nativeDriving.items]
  );

  const browseResources = useMemo<PresentableTaskResource[]>(() => {
    const ticResources = hasSearchQuery
      ? (searchData?.tasks ?? []).filter(
          (resource): resource is PresentableTaskResource => resource.type === 'task'
        )
      : browser.browseTasks;
    return uniqueTaskResourcesByUri([...ticResources, ...nativeTasks]);
  }, [hasSearchQuery, searchData, browser.browseTasks, nativeTasks]);

  // TT-5457: scope AND status are narrowed server-side, so the browse list renders as returned. A
  // client re-filter is what dropped deep subtasks and made later pages unreachable.
  const groupedOptions = useMemo<GroupedLinkedWorkOptions[]>(
    () => toGroupedOptions(browseResources),
    [browseResources]
  );

  // Scrolling to the end loads whichever source still has rows. TIC search is a single capped page,
  // so while searching only the native search pages further.
  const ticHasMore = !hasSearchQuery && browser.hasMore;
  const nativeHasMore = nativeDriving.hasMore;
  const { loadMore: loadMoreTic } = browser;
  const { loadMore: loadMoreNative, refresh: refreshNative } = nativeDriving;
  const loadMore = useCallback(() => {
    if (ticHasMore) loadMoreTic();
    if (nativeHasMore) loadMoreNative();
  }, [ticHasMore, nativeHasMore, loadMoreTic, loadMoreNative]);

  const setLinkedWorkMode = useCallback(
    (mode: TicLinkedWorkMode) => {
      // Persist the choice verbatim — including 'all' — so an explicit pick wins over the legacy
      // tic_filter_by_project fallback (storing 'all' as `false` would read back as "unset").
      dispatch(saveUserProperty(TIC_LINKED_WORK_SCOPE, mode));
    },
    [dispatch]
  );
  const setShowActive = useCallback(
    (checked: boolean) => {
      dispatch(saveUserProperty(TIC_SHOW_ACTIVE_TASKS, checked));
    },
    [dispatch]
  );
  const setShowCompleted = useCallback(
    (checked: boolean) => {
      dispatch(saveUserProperty(TIC_SHOW_COMPLETED_TASKS, checked));
    },
    [dispatch]
  );

  return {
    groupedOptions,
    flatOptions,
    isLoading,
    error,
    // Infinite scroll over both sources (TT-5457).
    hasMore: ticHasMore || nativeHasMore,
    loadMore,
    /**
     * Call when the picker opens. The picker stays mounted (and subscribed) for as long as its row
     * is on screen, so this is what lets native items created or edited meanwhile appear without a
     * reload; it re-fetches only once the loaded list is older than the refresh threshold.
     */
    refresh: refreshNative,
    scrollResetKey,
    /**
     * Nothing can be concluded from an empty list yet: a request is in flight, or none has settled.
     * `isLoading` alone is not enough — a skipped query never loads, so a mount whose TIC session is
     * still resolving would otherwise render as an authoritative empty. Both toggles off is the
     * opposite case and deliberately NOT resolving: it is knowable without asking the server.
     */
    isResolving: !scope.statusAdmitsNothing && (isTicResolving || isNativeResolving),
    // Filter state + setters for the picker's filter menu.
    filters: {
      linkedWorkMode,
      showActive,
      showCompleted,
      isProjectSelected: scope.isProjectSelected,
      sameIntegrationIsApplied: scope.sameIntegrationIsApplied,
      sameProjectIsApplied: scope.sameProjectIsApplied,
      selectedProjectToolId: scope.selectedProjectToolId,
      setLinkedWorkMode,
      setShowActive,
      setShowCompleted,
    },
  };
};
